// CarSavr analytics — dual-dispatches every event to BOTH PostHog and GA4.
//
// • PostHog is initialised in /app/frontend/public/index.html for funnel +
//   session-recording analysis.
// • GA4 (measurement ID G-E0R41XGJBW) is initialised in the same file with
//   `send_page_view: false` — RouteAnalytics fires page_view manually on
//   SPA route change.
//
// Why dual-dispatch instead of separate trackers? Single source of truth →
// no duplicate events, no drift between providers, every existing track()
// callsite (Footer newsletter, QuoteForm ZIP submit, ExitIntent modal,
// ArticlePage view, etc.) instantly mirrors to GA4 the moment this file
// updates. Safe to call before gtag/posthog finish loading — both calls
// are guarded so a transient script-load delay never breaks the UI.
//
// Iter-185.L — Consent gate. Every track() callsite is now gated on the
// user's analytics-cookie choice. Consent Mode v2 should already gate
// GA4 server-side, but we add a belt-and-braces client-side guard so
// PostHog (which doesn't honor Consent Mode) also stays quiet until
// the user opts in. The dev console mirror still fires regardless so
// engineers can verify wiring locally.

import { getConsent } from "./cookieConsent";
import { isSeoCheckFrame } from "./seoCheckFrame";

const safePosthog = () => (typeof window !== "undefined" ? window.posthog : null);
const safeGtag = () => (typeof window !== "undefined" ? window.gtag : null);
const analyticsAllowed = () => {
  try {
    return Boolean(getConsent()?.analytics);
  } catch {
    return false;
  }
};

// Iter-275.BA (Phase 5 #20 — Production Readiness Audit).
// Designated-canonical event routing — GA4 is the canonical source
// of truth for revenue/conversion events, PostHog is the canonical
// source of truth for engagement/session-replay events. Both
// providers ARE still dual-dispatched for events that need full
// coverage (page views, experiment exposures, modal opens) so the
// admin tooling that reads from either provider keeps working.
//
// WHY: pre-iter-275.BA every track() callsite hit BOTH providers.
// Revenue queries in GA4 were polluted with low-value engagement
// signal (scroll depth, search input, calculator drag). PostHog
// session-recording quota was burned on conversion events that the
// finance team only needs in GA4. The split below restores a clean
// revenue surface in GA4 + a clean engagement surface in PostHog
// without forcing every callsite to know which provider to target.
const GA4_ONLY_REVENUE_EVENTS = new Set([
  // Direct revenue moments
  "affiliate_click",
  "lender_fallback_click",
  "lead_capture",
  // High-intent conversion clicks
  "compare_offers_click",
  "compare_winner_apply_click",
  "compare_footer_winner_apply_click",
  "comparison_verdict_apply_click",
  "state_cheapest_carrier_apply_click",
  "refi_hero_preview_cta_click",
  "multi_quote_callout_click",
  "hero_cta_click",
  "hero_tab_cta_click",
  "hero_zip_submit",
  // Form-level success (conversion markers — failures stay dual-dispatched
  // so the admin alerting picks them up in BOTH providers).
  "zip_form_success",
  "newsletter_success",
  "modal_submit",
  "calculator_result_cta_click",
  "inline_quote_cta_click",
  "compare_continue_click",
  "email_capture_submitted",
]);

const POSTHOG_ONLY_ENGAGEMENT_EVENTS = new Set([
  // Article reading depth + interaction
  "article_scroll_depth",
  "article_glossary_click",
  "article_feedback_rating",
  "article_feedback_submitted",
  "heading_link_copy",
  "methodology_panel_opened",
  // Field-level drop-off (only PostHog renders these into the funnel
  // drop-off heatmap; GA4 hits its 25-params-per-event ceiling).
  "funnel_field_focus",
  "funnel_field_blur",
  "funnel_step_advance",
  // Retargeting audience seeds — these power Custom Audiences via
  // PostHog → Meta / Google Ads connectors, not GA4.
  "audience_funnel_abandoned",
  "audience_calculator_engaged_no_quote",
  "audience_article_binge_no_click",
  // Search + interaction signal
  "search_query",
  "search_result_click",
  "calculator_interact",
  "inline_quote_input",
  "inline_quote_result",
  "inline_quote_view",
  // Compare stash micro-interactions (engagement only — the apply
  // CTA above the stash already lives in GA4_ONLY).
  "compare_stash_add",
  "compare_stash_remove",
  "compare_stash_clear",
  "compare_stash_view",
  "compare_stash_hydrated",
]);

// Iter-275.BA — Route decision returned to track() so it dispatches
// to the right providers. Default routing is BOTH (no behaviour
// change for events not in either set).
function _routeFor(event) {
  if (GA4_ONLY_REVENUE_EVENTS.has(event)) return { ga4: true, posthog: false };
  if (POSTHOG_ONLY_ENGAGEMENT_EVENTS.has(event)) return { ga4: false, posthog: true };
  return { ga4: true, posthog: true };
}

/** Send an event to PostHog + GA4. Never throws — analytics MUST NOT break the UI.
 *  Iter-185.L — Gated on the analytics cookie consent. Returns silently
 *  when the user hasn't opted in. */
// Iter-423 — privacy choke point: analytics never receives raw ZIPs, exact
// incomes, or direct identifiers. ZIP → 3-digit prefix, income → band.
const _DROP_KEYS = new Set(["email", "phone", "first_name", "last_name", "full_name", "name", "address", "street", "dob", "date_of_birth", "vin", "ssn", "license_number"]);
const _ZIP_KEYS = new Set(["zip", "zip_code", "zipcode", "postal_code"]);
const _INCOME_KEYS = new Set(["income", "annual_income", "salary", "monthly_income"]);
const _incomeBand = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return "unknown";
  const annual = n < 20000 ? n * 12 : n;
  if (annual < 50000) return "<50k";
  if (annual < 75000) return "50-75k";
  if (annual < 100000) return "75-100k";
  if (annual < 150000) return "100-150k";
  return "150k+";
};
export const sanitizeAnalyticsProps = (props) => {
  if (!props || typeof props !== "object") return props;
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    const key = k.toLowerCase();
    if (_DROP_KEYS.has(key)) continue;
    if (_ZIP_KEYS.has(key)) {
      const d = String(v ?? "").replace(/\D/g, "");
      if (d.length >= 3) out.zip3 = d.slice(0, 3);
      continue;
    }
    if (_INCOME_KEYS.has(key)) {
      out.income_band = _incomeBand(v);
      continue;
    }
    out[k] = v;
  }
  return out;
};

export const track = (event, rawProps = {}) => {
  const props = sanitizeAnalyticsProps(rawProps);
  if (!analyticsAllowed()) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log("[analytics:gated]", event, props);
    }
    return;
  }
  // Iter-275.BA (Phase 5 #20) — Route decision per event.
  const route = _routeFor(event);
  // PostHog
  if (route.posthog) {
    try {
      const ph = safePosthog();
      if (ph && typeof ph.capture === "function") {
        ph.capture(event, props);
      }
    } catch (_) {
      /* swallow */
    }
  }
  // GA4 — gtag accepts any event name + arbitrary parameters. GA4's reporting
  // surface accepts ~25 custom params per event; we keep payloads under that.
  if (route.ga4) {
    try {
      const gtag = safeGtag();
      if (typeof gtag === "function") {
        gtag("event", event, props);
      }
    } catch (_) {
      /* swallow */
    }
  }
  // Iter-275.BA (Phase 5 #21) — Auto-fire `experiment_convert` for every
  // GA4 revenue event. Each active exposure stored in sessionStorage
  // becomes one experiment_convert event so the admin dashboard can
  // join experiment_view ↔ experiment_convert by experiment_key +
  // variant_key + visitor_id without callsites needing to know which
  // experiment they're participating in.
  if (GA4_ONLY_REVENUE_EVENTS.has(event)) {
    try {
      const raw =
        typeof window !== "undefined"
          ? window.sessionStorage.getItem("carsavr:exp-exposures")
          : null;
      const exposures = raw ? JSON.parse(raw) : {};
      const seen = new Set();
      Object.keys(exposures || {}).forEach((tag) => {
        // tag format: `<experimentKey>:<variantKey>`
        const idx = tag.indexOf(":");
        if (idx < 0 || seen.has(tag)) return;
        seen.add(tag);
        const experimentKey = tag.slice(0, idx);
        const variantKey = tag.slice(idx + 1);
        const convertPayload = {
          experiment_key: experimentKey,
          variant_key: variantKey,
          conversion_event: event,
        };
        try {
          const ph = safePosthog();
          if (ph && typeof ph.capture === "function") {
            ph.capture("experiment_convert", convertPayload);
          }
        } catch (_) {
          /* swallow */
        }
        try {
          const gtag = safeGtag();
          if (typeof gtag === "function") {
            gtag("event", "experiment_convert", convertPayload);
          }
        } catch (_) {
          /* swallow */
        }
        // Backend beacon for the admin Results panel.
        _beaconExperimentEvent({
          experiment_key: experimentKey,
          variant_key: variantKey,
          kind: "convert",
          visitor_id: _visitorIdForBeacon(),
          conversion_event: event,
        });
      });
    } catch (_) {
      /* swallow — auto-convert is best-effort observability */
    }
  }
  // Dev mirror so engineers can verify in the browser console.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("[analytics]", event, props);
  }
};

/** SPA page_view event for GA4. Called by RouteAnalytics on every
 *  react-router location change. Sends the canonical page_view shape GA4
 *  expects (page_path + page_location + page_title) so the report UI
 *  populates correctly without any custom-dimension setup.
 *
 *  Iter-185.L — Gated on the analytics cookie consent. */
export const trackPageView = (pathname, title) => {
  if (!analyticsAllowed()) return;
  try {
    const gtag = safeGtag();
    if (typeof gtag === "function") {
      gtag("event", "page_view", {
        page_path: pathname,
        page_location: typeof window !== "undefined" ? window.location.href : pathname,
        page_title: title || (typeof document !== "undefined" ? document.title : ""),
      });
    }
    // PostHog already auto-captures pageviews on route change via its
    // capture_pageview default, so we only need to bridge to GA4 here.
  } catch (_) {
    /* swallow */
  }
};

// Iter-275.BA — Exported for unit tests + admin observability tools.
export const _analyticsRouting = {
  GA4_ONLY_REVENUE_EVENTS,
  POSTHOG_ONLY_ENGAGEMENT_EVENTS,
  routeFor: _routeFor,
};

// Iter-275.BA (Phase 5 #21 — A/B harness completion).
// `experiment_view` fires the first time a user is bucketed into a
// variant for a given experiment in a given session (semantic
// upgrade from the iter-82 `experiment_exposure` name, which we
// keep firing for back-compat). `experiment_convert` is fired by
// callsites that detect a conversion event AND have a recent
// experiment exposure for the current visitor.
//
// Both events are dual-dispatched (default routing) so the admin
// experiment-results dashboard can query either provider.
//
// ALSO beacons to the backend `/api/experiments/beacon` endpoint so
// the admin Experiments Results panel can aggregate view + convert
// counts per variant without calling GA4/PostHog APIs. Beacon is
// fire-and-forget — a 4xx/5xx never breaks the page.
function _beaconExperimentEvent(payload) {
  if (typeof window === "undefined" || typeof fetch !== "function" || isSeoCheckFrame()) return;
  try {
    // Use sendBeacon when available — survives page unload, used by
    // PostHog + GA4 for the same reason. Falls back to fetch.
    const url = "/api/experiments/beacon";
    const body = JSON.stringify(payload);
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch (_) {
    /* swallow */
  }
}

function _visitorIdForBeacon() {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem("carsavr:visitor-id") || "";
  } catch (_) {
    return "";
  }
}

export const trackExperimentView = (experimentKey, variantKey, extraProps = {}) => {
  track("experiment_view", {
    experiment_key: experimentKey,
    variant_key: variantKey,
    ...extraProps,
  });
  if (analyticsAllowed()) {
    _beaconExperimentEvent({
      experiment_key: experimentKey,
      variant_key: variantKey,
      kind: "view",
      visitor_id: _visitorIdForBeacon(),
    });
  }
};

export const trackExperimentConvert = (experimentKey, variantKey, conversionEvent, extraProps = {}) => {
  track("experiment_convert", {
    experiment_key: experimentKey,
    variant_key: variantKey,
    conversion_event: conversionEvent,
    ...extraProps,
  });
  if (analyticsAllowed()) {
    _beaconExperimentEvent({
      experiment_key: experimentKey,
      variant_key: variantKey,
      kind: "convert",
      visitor_id: _visitorIdForBeacon(),
      conversion_event: conversionEvent || "",
    });
  }
};

/** Canonical event names. Add new ones here rather than scattering string
 *  literals across the codebase — keeps GA4's Events report tidy and avoids
 *  typo-induced duplicate event names (`cta_click` vs `cta_clicked`). */
export const events = {
  // Form / lead capture
  ZIP_FORM_VIEW: "zip_form_view",
  ZIP_FORM_SUBMIT: "zip_form_submit",
  ZIP_FORM_SUCCESS: "zip_form_success",
  ZIP_FORM_ERROR: "zip_form_error",
  // Iter-176 — Hero per-tab CTA experiment-tracking. Distinct from
  // ZIP_FORM_SUBMIT because we want to see the CTA-label conversion
  // funnel separately (tab selection → CTA click → submit success).
  HERO_TAB_SELECT: "hero_tab_select",
  HERO_TAB_CTA_CLICK: "hero_tab_cta_click",
  // Iter-185.BK (AUDIT #4) — Inline ZIP entry above-the-fold on
  // pillar pages. Distinct from ZIP_FORM_SUBMIT (modal funnel)
  // because we want to A/B measure conversion lift from
  // ABOVE-THE-FOLD CTA placement vs. modal-only entry.
  HERO_ZIP_SUBMIT: "hero_zip_submit",
  NEWSLETTER_SUBMIT: "newsletter_submit",
  NEWSLETTER_SUCCESS: "newsletter_success",
  CONTACT_FORM_SUBMIT: "contact_form_submit",
  // Generic CTAs
  CTA_CLICK: "cta_click",
  COMPARE_OFFERS_CLICK: "compare_offers_click",
  // Iter-195.B (T7) — Multi-quote callout above non-insurance rate
  // tables. Distinct from COMPARE_OFFERS_CLICK so we can A/B the
  // pre-table aggregator CTA against the per-row Apply buttons.
  MULTI_QUOTE_CALLOUT_CLICK: "multi_quote_callout_click",
  // Affiliate / revenue
  AFFILIATE_CLICK: "affiliate_click",
  LENDER_FALLBACK_CLICK: "lender_fallback_click",
  // Iter-275.AA (Phase J — analytics dedup) — `INTERNAL_NAV_CLICK` separates
  // internal SEO routing clicks (US-map state tiles, warranty state grid,
  // warranty city tiles, etc.) from `AFFILIATE_CLICK`, which is reserved for
  // clicks that resolve to external partner URLs. Previously these internal
  // navigation clicks fired AFFILIATE_CLICK, polluting GA4's revenue metric
  // with false-positive engagement events. The funnel attribution payload
  // (state_slug, page_location, kind) is preserved so admin dashboards can
  // still trace state-click → eventual partner conversion.
  INTERNAL_NAV_CLICK: "internal_nav_click",
  // Modal funnel
  MODAL_OPEN: "modal_open",
  MODAL_CLOSE: "modal_close",
  MODAL_CATEGORY_SWITCH: "modal_category_switch",
  COMPARE_STASH_ADD: "compare_stash_add",
  COMPARE_STASH_REMOVE: "compare_stash_remove",
  COMPARE_STASH_CLEAR: "compare_stash_clear",
  COMPARE_STASH_VIEW: "compare_stash_view",
  // Iter-96 — Shareable compare URL events. _HYDRATED fires when a visitor
  // lands with ?stash=... and we populate from URL. _SHARE fires when a
  // visitor clicks the in-modal Share button. Tracking both lets us
  // measure viral loops + which categories share best.
  COMPARE_STASH_HYDRATED: "compare_stash_hydrated",
  COMPARE_STASH_SHARE: "compare_stash_share",
  // Iter-196.W (C7 from audit) — Email-this-comparison event,
  // tracked separately from the copy-link share so we can measure
  // which share channel converts better per vertical.
  COMPARE_STASH_EMAIL: "compare_stash_email",
  // Iter-196.X (F-MON-01) — Editor's-Pick Apply CTA click. The
  // highest-intent moment in the funnel — visitor saw 3 carriers
  // side-by-side, editor picked a winner, visitor clicked Apply.
  // Tracked separately so we can measure end-to-end conversion from
  // "winner banner shown" → "QuoteModal opened" → "QuoteModal submit".
  COMPARE_WINNER_APPLY_CLICK: "compare_winner_apply_click",
  // Iter-196.X (F-MON-03) — /<vs>/ editorial verdict Apply CTA.
  // The verdict card now ships with two outline CTAs (one per pick
  // option). Each side fires a separate event so the funnel
  // attribution can tell us which pick converts higher per slug
  // (e.g. for lease-vs-finance, does "Find lenders for Finance"
  // beat "Find lenders for Lease" on click rate?). Slug + side +
  // category are all attached for cohort analysis in GA4.
  COMPARISON_VERDICT_APPLY_CLICK: "comparison_verdict_apply_click",
  // Iter-196.X (F-MON-13) — State-page "Switch to {cheapest}" CTA.
  // Multiplied across 50 insurance state surfaces. Single highest-
  // visibility state-page revenue moment — visitor sees state avg
  // + cheapest carrier + projected savings before deciding to apply.
  // Slug + carrier + projected savings $/% all attached so we can
  // measure which states convert best.
  STATE_CHEAPEST_CARRIER_APPLY_CLICK: "state_cheapest_carrier_apply_click",
  // Iter-123 (L-4) — CSV export from the compare modal.
  COMPARE_EXPORT_CSV: "compare_export_csv",
  COMPARE_WIZARD_INTENT: "compare_wizard_intent",
  COMPARE_WIZARD_SCENARIO: "compare_wizard_scenario",
  COMPARE_WIZARD_RESULT_CLICK: "compare_wizard_result_click",
  MODAL_SUBMIT: "modal_submit",
  LEAD_CAPTURE: "lead_capture",
  // Calculator funnel
  CALCULATOR_OPEN: "calculator_open",
  CALCULATOR_INTERACT: "calculator_interact",
  CALCULATOR_RESULT_CTA_CLICK: "calculator_result_cta_click",
  // Iter-136 — cluster calculator strip click on article + guide pages.
  // Surface = "cluster_calc_strip", payload includes article_slug,
  // cluster, calc_slug so we can A/B which cluster pairings drive the
  // most calculator engagement.
  CALCULATOR_CTA_CLICK: "calculator_cta_click",
  // Article / guide engagement
  ARTICLE_VIEW: "article_view",
  ARTICLE_SCROLL_DEPTH: "article_scroll_depth",
  ARTICLE_CTA_CLICK: "article_cta_click",
  // Iter-81 — inline glossary link click inside an article body.
  // Fired by ArticlePage delegated listener so we don't have to wrap
  // every auto-injected anchor with React refs. Slug + article slug
  // payload lets us see which terms drive the most discovery flow.
  ARTICLE_GLOSSARY_CLICK: "article_glossary_click",
  // Iter-185.AU — Article feedback microsurvey
  ARTICLE_FEEDBACK_RATING: "article_feedback_rating",
  ARTICLE_FEEDBACK_SUBMITTED: "article_feedback_submitted",
  HEADING_LINK_COPY: "heading_link_copy",
  // Inline quote widget funnel (iter-58 — measure actual lift of the
  // iter-56 revenue lever now that the route bugs are fixed in iter-57)
  INLINE_QUOTE_VIEW: "inline_quote_view",
  INLINE_QUOTE_INPUT: "inline_quote_input",
  INLINE_QUOTE_RESULT: "inline_quote_result",
  INLINE_QUOTE_CTA_CLICK: "inline_quote_cta_click",
  // Search
  SEARCH_OPEN: "search_open",
  SEARCH_QUERY: "search_query",
  SEARCH_RESULT_CLICK: "search_result_click",
  // Iter-111 — Quote-success card UX events. Granular instrumentation
  // lets us measure email-capture conversion rate, skip rate, and
  // affiliate-routing click-through on the truthful new success card.
  COMPARE_CONTINUE_CLICK: "compare_continue_click",
  EMAIL_CAPTURE_SUBMITTED: "email_capture_submitted",
  EMAIL_CAPTURE_SKIPPED: "email_capture_skipped",
  EMAIL_CAPTURE_OPENED: "email_capture_opened",
  // Iter-113 — Welcome-back saved-quote retention events.
  BANNER_RESUME_CLICK: "banner_resume_click",
  SAVINGS_ESTIMATE_HYDRATED: "savings_estimate_hydrated",
  // Iter-116 — Methodology accordion opened from the right-column bridge.
  METHODOLOGY_PANEL_OPENED: "methodology_panel_opened",
  // Iter-185.BM (AUDIT #10) — PostHog retargeting audience seeds. Each
  // event is a PostHog audience anchor: "Users who fired this event
  // within the past N days but not the corresponding conversion event"
  // becomes the Custom Audience pushed to Meta / Google Ads.
  // See `/app/memory/POSTHOG_AUDIENCES.md` for the audience definitions
  // + retargeting playbook.
  AUDIENCE_FUNNEL_ABANDONED: "audience_funnel_abandoned",
  AUDIENCE_CALCULATOR_ENGAGED_NO_QUOTE: "audience_calculator_engaged_no_quote",
  AUDIENCE_ARTICLE_BINGE_NO_CLICK: "audience_article_binge_no_click",
  // Iter-185.BM (AUDIT #2) — Field-level drop-off instrumentation.
  // Fires on every form field focus / blur and on step advances inside
  // the multi-step intake forms. Drives the /admin/funnel/drop-off
  // dashboard so we can spot the leakiest field per category.
  FUNNEL_FIELD_FOCUS: "funnel_field_focus",
  FUNNEL_FIELD_BLUR: "funnel_field_blur",
  FUNNEL_STEP_ADVANCE: "funnel_step_advance",
  // Iter-185.BN (AUDIT #8) — Programmatic carrier × state SEO pages.
  // Fires on /auto-insurance/<state>/<carrier> view + hero CTA click
  // so the funnel-attribution dashboard can roll up the new traffic
  // surface into the cluster-attribution model.
  PROGRAMMATIC_PAGE_VIEW: "programmatic_page_view",
  HERO_CTA_CLICK: "hero_cta_click",
  // Iter-196.Z (F-CR-14 from audit) — Refinance hero inline savings
  // preview CTA. Distinct from the below-hero EmbeddedRefinanceEstimator
  // CTA so we can measure the hero variant's conversion lift over
  // visitors who scrolled past it to the full estimator. Payload:
  // current_apr, target_apr, apr_drop_points, modeled_balance,
  // modeled_term_months, monthly_delta, lifetime_savings, route_path.
  REFI_HERO_PREVIEW_CTA: "refi_hero_preview_cta_click",
  // Iter-196.Z (F-CR-18 from audit) — Compare modal FOOTER winner-apply
  // CTA. Distinct from COMPARE_WINNER_APPLY_CLICK (banner CTA above the
  // table) so the funnel-attribution dashboard can roll up which
  // position (banner vs. footer) converts higher.
  COMPARE_FOOTER_WINNER_APPLY_CLICK: "compare_footer_winner_apply_click",
  // Iter-275.AB (Phase K — network/error UX) — Production render errors
  // caught by ErrorBoundary now fire this event so the admin team learns
  // about user-facing failures BEFORE someone emails support. The payload
  // (name, message, component, page_path) is intentionally truncated to
  // keep under GA4's 100-byte-per-string + 25-params-per-event limits.
  UNHANDLED_ERROR: "unhandled_error",
};
